import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Divider,
  Chip,
  Switch
} from '@mui/material';
import {
  Close as CloseIcon,
  Store as StoreIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { API, graphqlOperation } from 'aws-amplify';
import { roleDefinitionsByClient } from 'graphql/queries';

/**
 * Modal for adding new assignments to an employee
 */
function AddAssignmentModal({
  open,
  onClose,
  employee,
  onAdd,
  locations = [],
  orgUnits = [],
  hierarchyDefinitions = [],
  loading = false,
  clientId
}) {
  const [assignmentType, setAssignmentType] = useState('location');
  const [selectedOrgLevel, setSelectedOrgLevel] = useState('');
  const [formData, setFormData] = useState({
    locationId: null,
    orgUnitId: null,
    orgLevel: '',
    roleDefinitionId: null,
    effectiveDate: new Date(),
    isPrimary: false,
    isActive: true,
    assignmentReason: ''
  });
  const [errors, setErrors] = useState({});

  // Fetch role definitions
  const { data: allRoleDefinitions = [] } = useQuery({
    queryKey: ['roleDefinitions', clientId],
    queryFn: async () => {
      const result = await API.graphql(
        graphqlOperation(roleDefinitionsByClient, {
          clientId: clientId,
          limit: 500
        })
      );
      return result.data.roleDefinitionsByClient?.items || [];
    },
    enabled: !!clientId && open,
    staleTime: 30 * 60 * 1000
  });

  // Get unique org levels from hierarchy definitions
  const availableOrgLevels = React.useMemo(() => {
    if (!hierarchyDefinitions || hierarchyDefinitions.length === 0) return [];
    return hierarchyDefinitions
      .filter(h => h.orgLevel && h.orgLevel !== 'LOCATION_LEVEL')
      .map(h => ({
        value: h.orgLevel,
        label: h.labelSingular || h.orgLevel
      }));
  }, [hierarchyDefinitions]);

  // Filter role definitions based on assignment type and org level
  const filteredRoleDefinitions = React.useMemo(() => {
    if (!allRoleDefinitions || allRoleDefinitions.length === 0) return [];

    if (assignmentType === 'location') {
      // For location assignments, only show LOCATION_LEVEL roles
      return allRoleDefinitions.filter(r => r.assignmentLevel === 'LOCATION_LEVEL' && r.isActive !== false);
    } else if (assignmentType === 'orgUnit') {
      // For org unit assignments
      // Determine the effective org level
      let effectiveOrgLevel = selectedOrgLevel;

      // If only one org level exists, use it
      if (!effectiveOrgLevel && availableOrgLevels.length === 1) {
        effectiveOrgLevel = availableOrgLevels[0].value;
      }

      // If an org unit is selected, use its org level
      if (formData.orgUnitId) {
        const selectedUnit = orgUnits.find(o => o.id === formData.orgUnitId);
        if (selectedUnit?.orgLevel) {
          effectiveOrgLevel = selectedUnit.orgLevel;
        }
      }

      // Filter roles by the effective org level
      if (effectiveOrgLevel) {
        return allRoleDefinitions.filter(r => r.assignmentLevel === effectiveOrgLevel && r.isActive !== false);
      }
    }

    // If org unit but no level determined yet, return empty
    return [];
  }, [allRoleDefinitions, assignmentType, selectedOrgLevel, availableOrgLevels, formData.orgUnitId, orgUnits]);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setAssignmentType('location');
      setSelectedOrgLevel('');
      setFormData({
        locationId: null,
        orgUnitId: null,
        orgLevel: '',
        roleDefinitionId: null,
        effectiveDate: new Date(),
        isPrimary: false,
        isActive: true,
        assignmentReason: ''
      });
      setErrors({});

      // Auto-select org level if only one exists
      if (availableOrgLevels.length === 1) {
        setSelectedOrgLevel(availableOrgLevels[0].value);
      }
    }
  }, [open, availableOrgLevels]);

  // Handle assignment type change
  const handleTypeChange = (event, newType) => {
    if (newType) {
      setAssignmentType(newType);
      // Clear type-specific fields
      setFormData(prev => ({
        ...prev,
        locationId: null,
        orgUnitId: null,
        orgLevel: '',
        roleDefinitionId: null // Reset role when switching types
      }));
      setErrors({});

      // Handle org level selection for org unit type
      if (newType === 'orgUnit') {
        // Auto-select org level if only one exists
        if (availableOrgLevels.length === 1) {
          setSelectedOrgLevel(availableOrgLevels[0].value);
        } else {
          setSelectedOrgLevel('');
        }
      } else {
        setSelectedOrgLevel('');
      }
    }
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (assignmentType === 'location') {
      if (!formData.locationId) {
        newErrors.locationId = 'Location is required';
      }
    } else {
      // For org unit assignments
      if (availableOrgLevels.length > 1 && !selectedOrgLevel) {
        newErrors.orgLevel = 'Organization level is required';
      }
      if (!formData.orgUnitId) {
        newErrors.orgUnitId = 'Organization unit is required';
      }
    }

    if (!formData.roleDefinitionId) {
      newErrors.roleDefinitionId = 'Role is required';
    }

    if (!formData.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const selectedRole = filteredRoleDefinitions.find(r => r.id === formData.roleDefinitionId);

    const assignmentData = {
      type: assignmentType,
      employeeId: employee.id,
      effectiveDate: formData.effectiveDate.toISOString(),
      roleDefinitionId: formData.roleDefinitionId,
      roleSnapshot: selectedRole?.roleName || '',
      isPrimary: formData.isPrimary,
      isActive: formData.isActive,
      assignmentReason: formData.assignmentReason,
      employeeNameSnapshot: `${employee.firstName} ${employee.lastName}`,
      clientId: clientId
    };

    if (assignmentType === 'location') {
      const selectedLocation = locations.find(l => l.id === formData.locationId);
      assignmentData.locationId = formData.locationId;
      assignmentData.locationNameSnapshot = selectedLocation?.locationName || '';
    } else {
      const selectedOrgUnit = orgUnits.find(o => o.id === formData.orgUnitId);
      assignmentData.orgUnitId = formData.orgUnitId;
      assignmentData.orgUnitName = selectedOrgUnit?.name;
      assignmentData.orgLevel = selectedOrgUnit?.orgLevel || formData.orgLevel;
    }

    onAdd(assignmentData);
  };

  // Get org level label
  const getOrgLevelLabel = (orgLevel) => {
    const definition = hierarchyDefinitions.find(d => d.orgLevel === orgLevel);
    return definition?.labelSingular || orgLevel;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Assignment</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Assign {employee?.firstName} {employee?.lastName} to a location or organizational unit
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Assignment Type Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Assignment Type
          </Typography>
          <ToggleButtonGroup
            value={assignmentType}
            exclusive
            onChange={handleTypeChange}
            fullWidth
            sx={{ mt: 1 }}
          >
            <ToggleButton value="location">
              <StoreIcon sx={{ mr: 1 }} />
              Location Assignment
            </ToggleButton>
            <ToggleButton value="orgUnit">
              <BusinessIcon sx={{ mr: 1 }} />
              Organizational Unit
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Location Assignment Fields */}
        {assignmentType === 'location' && (
          <>
            <Autocomplete
              options={locations}
              getOptionLabel={(option) => option.locationName || ''}
              value={locations.find(l => l.id === formData.locationId) || null}
              onChange={(event, newValue) => handleFieldChange('locationId', newValue?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  required
                  error={!!errors.locationId}
                  helperText={errors.locationId}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2">{option.locationName}</Typography>
                </Box>
              )}
/>
          </>
        )}

        {/* Org Unit Assignment Fields */}
        {assignmentType === 'orgUnit' && (
          <>
            {/* Org Level Selector - only show if multiple levels exist */}
            {availableOrgLevels.length > 1 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="org-level-select-label">Organization Level *</InputLabel>
                <Select
                  labelId="org-level-select-label"
                  value={selectedOrgLevel}
                  onChange={(e) => {
                    setSelectedOrgLevel(e.target.value);
                    // Clear role selection when org level changes
                    handleFieldChange('roleDefinitionId', null);
                  }}
                  label="Organization Level *"
                  required
                >
                  {availableOrgLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Show single org level as read-only if only one exists */}
            {availableOrgLevels.length === 1 && (
              <TextField
                label="Organization Level"
                value={availableOrgLevels[0].label}
                disabled
                fullWidth
                sx={{ mb: 2 }}
              />
            )}

            {/* Org Unit Selector - filtered by selected org level */}
            <Autocomplete
              options={selectedOrgLevel ? orgUnits.filter(o => o.orgLevel === selectedOrgLevel) : orgUnits}
              getOptionLabel={(option) => option.name || ''}
              value={orgUnits.find(o => o.id === formData.orgUnitId) || null}
              onChange={(event, newValue) => {
                handleFieldChange('orgUnitId', newValue?.id);
                handleFieldChange('orgLevel', newValue?.orgLevel || selectedOrgLevel);
              }}
              disabled={availableOrgLevels.length > 1 && !selectedOrgLevel}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organizational Unit"
                  required
                  error={!!errors.orgUnitId}
                  helperText={errors.orgUnitId || (availableOrgLevels.length > 1 && !selectedOrgLevel ? 'Select an organization level first' : '')}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              )}
            />
          </>
        )}

        {/* Common Fields */}
        <Autocomplete
          options={filteredRoleDefinitions}
          getOptionLabel={(option) => option.roleName || ''}
          value={filteredRoleDefinitions.find(r => r.id === formData.roleDefinitionId) || null}
          onChange={(event, newValue) => handleFieldChange('roleDefinitionId', newValue?.id)}
          disabled={assignmentType === 'orgUnit' && availableOrgLevels.length > 1 && !selectedOrgLevel}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Role"
              required
              error={!!errors.roleDefinitionId}
              helperText={
                errors.roleDefinitionId ||
                (assignmentType === 'orgUnit' && availableOrgLevels.length > 1 && !selectedOrgLevel ? 'Select an organization level first' :
                 filteredRoleDefinitions.length === 0 && (assignmentType === 'location' || selectedOrgLevel || availableOrgLevels.length === 1) ?
                 `No active roles available for ${assignmentType === 'location' ? 'locations' : 'this organization level'}` : '')
              }
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Typography variant="body2">{option.roleName}</Typography>
            </Box>
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Effective Date"
              value={formData.effectiveDate}
              onChange={(newValue) => handleFieldChange('effectiveDate', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={!!errors.effectiveDate}
                  helperText={errors.effectiveDate}
                  fullWidth
                />
              )}
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isPrimary}
                onChange={(e) => handleFieldChange('isPrimary', e.target.checked)}
              />
            }
            label="Primary"
            sx={{ minWidth: 120 }}
          />
        </Box>

        <TextField
          label="Assignment Reason"
          value={formData.assignmentReason}
          onChange={(e) => handleFieldChange('assignmentReason', e.target.value)}
          multiline
          rows={2}
          fullWidth
          sx={{ mb: 2 }}
          helperText="Optional: Provide a reason for this assignment"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <AdminButton
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </AdminButton>
        <AdminButton
          variant="contained"
          onClick={handleSave}
          disabled={loading}
        >
          Add Assignment
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

AddAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employee: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  locations: PropTypes.array,
  orgUnits: PropTypes.array,
  hierarchyDefinitions: PropTypes.array,
  loading: PropTypes.bool,
  clientId: PropTypes.string.isRequired
};

export default AddAssignmentModal;